import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { makeStyles } from '@material-ui/core/styles';

import { mdiClose } from '@mdi/js';

import { pantonFonts } from 'components/theme/common';
import { getRedesignDialogColors } from './redesignDialogColors';
import RedesignDialogIconButton from './RedesignDialogIconButton';

const useStyles = makeStyles(
  (theme) => {
    const colors = getRedesignDialogColors(theme);
    const xs = theme.breakpoints.down('xs');
    const ellipsis = {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    };
    return {
      // Flat, on the paper, a hairline under it; it never scrolls (the body
      // does), so on a phone it stays at the top.
      header: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        flexShrink: 0,
        boxSizing: 'border-box',
        minHeight: 64,
        padding: theme.spacing(1.5, 2, 1.5, 3),
        borderBottom: `1px solid ${colors.border}`,
        [xs]: {
          minHeight: 56,
          padding: theme.spacing(1, 1, 1, 2),
        },
      },
      headings: {
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        flex: '1 1 auto',
        minWidth: 0,
      },
      titleRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        minWidth: 0,
      },
      leading: {
        display: 'flex',
        alignItems: 'center',
        flexShrink: 0,
      },
      // Panton for the title as the redesigns' headings, its fontFamily only
      // (the theme's fontPanton helpers carry a colour and a line height).
      title: {
        ...ellipsis,
        margin: 0,
        minWidth: 0,
        fontFamily: pantonFonts.panton,
        fontWeight: 'normal',
        fontSize: '1.25rem',
        lineHeight: 1.3,
        color: colors.text,
        [xs]: {
          fontSize: '1.125rem',
        },
      },
      subtitle: {
        ...ellipsis,
        fontFamily: pantonFonts.panton,
        fontSize: '0.8125rem',
        lineHeight: 1.4,
        color: colors.muted,
      },
      actions: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.5),
        flexShrink: 0,
      },
    };
  },
  { name: 'LixRedesignDialogHeader' }
);

/**
 * The dialog's header: [leading] title (and the subtitle under it), the
 * header actions, the close button (unless `onClose` is null).
 */
function RedesignDialogHeader({
  titleId,
  title,
  subtitle,
  leading,
  actions,
  onClose,
}) {
  const classes = useStyles();
  const { t } = useTranslation('common');
  return (
    <div className={classes.header} data-redesign-dialog-header="true">
      <div className={classes.headings}>
        <div className={classes.titleRow}>
          {leading ? <span className={classes.leading}>{leading}</span> : null}
          <h2 id={titleId} className={classes.title}>
            {title}
          </h2>
        </div>
        {subtitle ? <div className={classes.subtitle}>{subtitle}</div> : null}
      </div>
      {actions ? <div className={classes.actions}>{actions}</div> : null}
      {onClose ? (
        <RedesignDialogIconButton
          icon={mdiClose}
          iconSize={1}
          aria-label={t('common.close')}
          onClick={onClose}
          data-redesign-dialog-close="true"
        />
      ) : null}
    </div>
  );
}

RedesignDialogHeader.propTypes = {
  titleId: PropTypes.string.isRequired,
  title: PropTypes.node.isRequired,
  subtitle: PropTypes.node,
  leading: PropTypes.node,
  actions: PropTypes.node,
  onClose: PropTypes.func,
};

export default RedesignDialogHeader;
